package com.fantasytenis.web.hooks

import androidx.compose.runtime.*
import com.fantasytenis.shared.core.realtime.*
import com.fantasytenis.shared.core.utils.SharedData
import com.fantasytenis.shared.domain.scoring.entity.LiveScoreEntity
import com.fantasytenis.shared.domain.scoring.entity.MatchPlayerScore
import com.fantasytenis.shared.domain.scoring.entity.LiveMatchStatus
import com.fantasytenis.web.di.DependencyContainer
import kotlinx.browser.window
import kotlinx.coroutines.launch

// External console for logging
private val console: dynamic get() = window.asDynamic().console

/**
 * Composable hook for managing realtime connection state.
 *
 * Automatically connects when component mounts and disconnects on unmount.
 * Provides subscription helpers for different entity types.
 */
@Composable
fun useRealtime(): RealtimeState {
    val scope = rememberCoroutineScope()
    val connection = DependencyContainer.realtimeConnection

    var connectionState by remember { mutableStateOf(connection.connectionState.value) }
    var isConnecting by remember { mutableStateOf(false) }

    // Connect on mount
    LaunchedEffect(Unit) {
        if (!connection.isConnected && !isConnecting) {
            isConnecting = true
            try {
                val token = SharedData.accessToken.value
                connection.connect(token)
            } catch (e: Exception) {
                // Socket.IO library may not be loaded - gracefully handle
                connectionState = ConnectionState.ERROR
                console.log("Realtime connection failed: ${e.message}")
            }
            isConnecting = false
        }

        // Listen to connection state changes
        connection.onConnectionStateChange { state ->
            connectionState = state
            SharedData.connectedToRealtime.value = state == ConnectionState.CONNECTED
        }
    }

    // Update SharedData with live scores
    DisposableEffect(Unit) {
        connection.onLiveScore { payload ->
            val entity = convertPayloadToEntity(payload)
            SharedData.updateLiveScore(payload.matchId, entity)
            console.log("Live score updated for match ${payload.matchId}: ${payload.setScore}")
        }

        onDispose {
            // Don't disconnect - other screens might need the connection
        }
    }

    return remember(connectionState) {
        RealtimeState(
            isConnected = connectionState == ConnectionState.CONNECTED,
            connectionState = connectionState,
            connect = {
                scope.launch {
                    val token = SharedData.accessToken.value
                    connection.connect(token)
                }
            },
            disconnect = {
                scope.launch {
                    connection.disconnect()
                }
            }
        )
    }
}

/**
 * State holder for realtime connection.
 */
data class RealtimeState(
    val isConnected: Boolean,
    val connectionState: ConnectionState,
    val connect: () -> Unit,
    val disconnect: () -> Unit
)

/**
 * Hook for subscribing to bracket challenge updates.
 *
 * @param challengeId The bracket challenge ID to subscribe to
 * @param onLeaderboardUpdate Callback when leaderboard updates are received
 */
@Composable
fun useBracketSubscription(
    challengeId: String,
    onLeaderboardUpdate: (BracketLeaderboardPayload) -> Unit
) {
    val scope = rememberCoroutineScope()
    val connection = DependencyContainer.realtimeConnection

    LaunchedEffect(challengeId) {
        // Subscribe to bracket updates
        if (connection.isConnected) {
            connection.subscribeToBracket(challengeId)
        }

        // Set up leaderboard listener
        connection.onBracketLeaderboard { update ->
            if (update.challengeId == challengeId) {
                onLeaderboardUpdate(update)
            }
        }
    }

    // Cleanup on unmount
    DisposableEffect(challengeId) {
        onDispose {
            scope.launch {
                connection.unsubscribeFromBracket(challengeId)
            }
        }
    }
}

/**
 * Hook for subscribing to contest updates.
 *
 * @param contestId The contest ID to subscribe to
 * @param onLeaderboardUpdate Callback when leaderboard updates are received
 * @param onEntryUpdate Callback when user entry updates are received
 */
@Composable
fun useContestSubscription(
    contestId: String,
    onLeaderboardUpdate: (ContestLeaderboardPayload) -> Unit,
    onEntryUpdate: ((UserEntryUpdatePayload) -> Unit)? = null
) {
    val scope = rememberCoroutineScope()
    val connection = DependencyContainer.realtimeConnection

    LaunchedEffect(contestId) {
        // Subscribe to contest updates
        if (connection.isConnected) {
            connection.subscribeToContest(contestId)
        }

        // Set up leaderboard listener
        connection.onContestLeaderboard { update ->
            if (update.contestId == contestId) {
                onLeaderboardUpdate(update)
            }
        }

        // Set up entry update listener if callback provided
        onEntryUpdate?.let { callback ->
            connection.onUserEntryUpdate { update ->
                if (update.contestId == contestId) {
                    callback(update)
                }
            }
        }
    }

    // Cleanup on unmount
    DisposableEffect(contestId) {
        onDispose {
            scope.launch {
                connection.unsubscribeFromContest(contestId)
            }
        }
    }
}

/**
 * Hook for subscribing to match updates.
 *
 * @param matchId The match ID to subscribe to
 * @param onScoreUpdate Callback when score updates are received
 */
@Composable
fun useMatchSubscription(
    matchId: String,
    onScoreUpdate: (LiveScorePayload) -> Unit
) {
    val scope = rememberCoroutineScope()
    val connection = DependencyContainer.realtimeConnection

    LaunchedEffect(matchId) {
        console.log("[WS] useMatchSubscription for matchId: $matchId")
        console.log("[WS] Connection state: ${connection.isConnected}")

        // Subscribe to match updates
        if (connection.isConnected) {
            console.log("[WS] Subscribing to match...")
            val success = connection.subscribeToMatch(matchId)
            console.log("[WS] Subscription result: $success")
        } else {
            console.log("[WS] Cannot subscribe - not connected")
        }

        // Set up score listener
        connection.onLiveScore { score ->
            console.log("[WS] Received score event for: ${score.matchId}")
            if (score.matchId == matchId) {
                console.log("[WS] Score matches our match!")
                onScoreUpdate(score)
            }
        }
    }

    // Re-subscribe when connection becomes available
    LaunchedEffect(connection.isConnected, matchId) {
        if (connection.isConnected) {
            console.log("[WS] Connection now available, subscribing to: $matchId")
            connection.subscribeToMatch(matchId)
        }
    }

    // Cleanup on unmount
    DisposableEffect(matchId) {
        onDispose {
            scope.launch {
                console.log("[WS] Unsubscribing from: $matchId")
                connection.unsubscribeFromMatch(matchId)
            }
        }
    }
}

/**
 * Hook for listening to system announcements.
 *
 * @param onAnnouncement Callback when announcements are received
 * @param onMaintenance Callback when maintenance notices are received
 */
@Composable
fun useSystemNotifications(
    onAnnouncement: ((AnnouncementPayload) -> Unit)? = null,
    onMaintenance: ((MaintenancePayload) -> Unit)? = null
) {
    val connection = DependencyContainer.realtimeConnection

    LaunchedEffect(Unit) {
        onAnnouncement?.let { callback ->
            connection.onAnnouncement(callback)
        }

        onMaintenance?.let { callback ->
            connection.onMaintenanceNotice(callback)
        }
    }
}

/**
 * Hook for subscribing to survivor contest updates.
 *
 * @param contestId The survivor contest ID to subscribe to
 * @param onLeaderboardUpdate Callback when leaderboard updates are received
 * @param onPickResult Callback when pick results are received
 * @param onEliminationUpdate Callback when elimination events occur
 */
@Composable
fun useSurvivorSubscription(
    contestId: String,
    onLeaderboardUpdate: ((SurvivorLeaderboardPayload) -> Unit)? = null,
    onPickResult: ((SurvivorPickResultPayload) -> Unit)? = null,
    onEliminationUpdate: ((SurvivorEliminationPayload) -> Unit)? = null
) {
    val scope = rememberCoroutineScope()
    val connection = DependencyContainer.realtimeConnection

    LaunchedEffect(contestId) {
        // Subscribe to survivor contest updates
        if (connection.isConnected) {
            connection.subscribeToSurvivor(contestId)
        }

        // Set up leaderboard listener
        onLeaderboardUpdate?.let { callback ->
            connection.onSurvivorLeaderboard { update ->
                if (update.contestId == contestId) {
                    callback(update)
                }
            }
        }

        // Set up pick result listener
        onPickResult?.let { callback ->
            connection.onSurvivorPickResult { result ->
                if (result.contestId == contestId) {
                    callback(result)
                }
            }
        }

        // Set up elimination listener
        onEliminationUpdate?.let { callback ->
            connection.onSurvivorElimination { elimination ->
                if (elimination.contestId == contestId) {
                    callback(elimination)
                }
            }
        }
    }

    // Cleanup on unmount
    DisposableEffect(contestId) {
        onDispose {
            scope.launch {
                connection.unsubscribeFromSurvivor(contestId)
            }
        }
    }
}

/**
 * Convert LiveScorePayload from WebSocket to LiveScoreEntity for UI.
 *
 * Payload format:
 * - setScore: "2-1" (player1 sets won - player2 sets won)
 * - gameScore: "3-2" (player1 games - player2 games in current set)
 * - pointScore: "40-30" or "AD-40" (current game points)
 * - setScores: [{homeScore: 6, awayScore: 4, setNumber: 1}, ...] (individual set scores)
 */
private fun convertPayloadToEntity(payload: LiveScorePayload): LiveScoreEntity {
    // Parse game score (e.g., "3-2" -> 3, 2) for current set
    val gameParts = payload.gameScore.split("-").map { it.trim().toIntOrNull() ?: 0 }
    val player1Games = gameParts.getOrElse(0) { 0 }
    val player2Games = gameParts.getOrElse(1) { 0 }

    // Parse point score (e.g., "40-30" -> "40", "30")
    val pointParts = payload.pointScore.split("-").map { it.trim() }
    val player1Points = pointParts.getOrElse(0) { "0" }
    val player2Points = pointParts.getOrElse(1) { "0" }

    // Build set scores from the payload's setScores array
    // Each entry contains games won by each player in that set
    // Add the current set's games as the last entry
    val completedSetScoresPlayer1 = payload.setScores.map { it.homeScore }
    val completedSetScoresPlayer2 = payload.setScores.map { it.awayScore }

    // Full score list: completed sets + current set in progress
    val player1SetScores = completedSetScoresPlayer1 + listOf(player1Games)
    val player2SetScores = completedSetScoresPlayer2 + listOf(player2Games)

    // Current set number
    val currentSet = payload.setScores.size + 1

    // Determine who is serving
    val serving = if (payload.serverId == payload.player1Id) "player1" else "player2"

    // Map status string to enum
    val status = when (payload.status.lowercase()) {
        "live", "in_progress", "playing", "inprogress" -> LiveMatchStatus.IN_PROGRESS
        "completed", "finished" -> LiveMatchStatus.COMPLETED
        "suspended", "interrupted", "delayed" -> LiveMatchStatus.SUSPENDED
        "retired", "walkover" -> LiveMatchStatus.RETIRED
        else -> LiveMatchStatus.IN_PROGRESS
    }

    // Handle tiebreak points
    val tiebreakScore = payload.tiebreakScore
    val player1TiebreakPoints = if (payload.isTiebreak && tiebreakScore != null) {
        tiebreakScore.split("-").getOrElse(0) { "0" }.toIntOrNull()
    } else null

    val player2TiebreakPoints = if (payload.isTiebreak && tiebreakScore != null) {
        tiebreakScore.split("-").getOrElse(1) { "0" }.toIntOrNull()
    } else null

    return LiveScoreEntity(
        matchId = payload.matchId,
        player1 = MatchPlayerScore(
            playerId = payload.player1Id,
            playerName = payload.player1Name,
            sets = player1SetScores,
            games = player1Games,
            points = player1Points,
            tiebreakPoints = player1TiebreakPoints
        ),
        player2 = MatchPlayerScore(
            playerId = payload.player2Id,
            playerName = payload.player2Name,
            sets = player2SetScores,
            games = player2Games,
            points = player2Points,
            tiebreakPoints = player2TiebreakPoints
        ),
        currentSet = currentSet,
        currentGame = player1Games + player2Games + 1,
        serving = serving,
        status = status,
        fantasyPoints = emptyMap() // Fantasy points calculated separately
    )
}
